/**
 * Evidence Access Management JavaScript
 * 
 * Handles user access management for evidence objects including:
 * - Adding users to evidence
 * - Managing user roles
 * - Inviting external users
 * - Managing pending invitations
 */

// Global variables
let currentEvidenceId = null;

/**
 * Show the evidence user access modal
 * @param {string} evidenceId - The evidence ID
 */
function showEvidenceUserAccessForm(evidenceId) {
    currentEvidenceId = evidenceId;
    
    // Set the evidence ID in the modal's hidden input
    const evidenceIdInput = document.getElementById('evidence_id');
    if (evidenceIdInput) {
        evidenceIdInput.value = evidenceId;
        console.log('Evidence Access Debug - Set evidence_id in modal input:', evidenceId);
    } else {
        console.warn('Evidence Access Debug - evidence_id input not found in modal');
    }
    
    // Clear any previous form data from the modal
    const emailInput = document.getElementById('evidence_user_email');
    if (emailInput) {
        emailInput.value = '';
        console.log('Evidence Access Debug - Cleared email input');
    }
    
    // Clear all permission checkboxes
    document.querySelectorAll('input[name="permissions[]"]').forEach(checkbox => {
        checkbox.checked = false;
    });
    console.log('Evidence Access Debug - Cleared permission checkboxes');
    
    // Clear any existing alerts
    clearEvidenceAlerts();
    
    // Hide all alert messages (legacy)
    document.querySelectorAll('.permissions-user-not-found, .permissions-user-already-exists, .permissions-user-not-enterprise').forEach(alert => {
        alert.style.display = 'none';
    });
    
    // Show the modal
    const modal = new bootstrap.Modal(document.getElementById('evidenceUserAccessModal'));
    modal.show();
}

/**
 * Load the user access form
 * @param {string} evidenceId - The evidence ID
 */
function loadEvidenceUserAccessForm(evidenceId) {
    const formContainer = document.getElementById('evidence-user-access-form-container');
    
    // Create form HTML
    const formHtml = `
        <div class="card">
            <div class="card-header">
                <h6 class="mb-0">Add User Access</h6>
            </div>
            <div class="card-body">
                <form id="evidence-user-access-form" data-evidence-id="${evidenceId}">
                    <input type="hidden" name="evidence_access_nonce" value="${evidenceData.evidence_access_nonce}">
                    <div class="row">
                        <div class="col-md-6">
                            <div class="mb-3">
                                <label for="user_email" class="form-label">User Email</label>
                                <input type="email" class="form-control" id="user_email" name="user_email" required>
                                <div class="form-text">Enter the email address of the user to invite.</div>
                            </div>
                        </div>
                        <div class="col-md-4">
                            <div class="mb-3">
                                <label for="user_role" class="form-label">Role</label>
                                <select class="form-select" id="user_role" name="role" required>
                                    <option value="">Select Role</option>
                                    <option value="admin">Admin - Full access to edit evidence & users</option>
                                    <option value="read_write">Read/Write - Access to project data only</option>
                                    <option value="read_only">Read Only - Read project data access only</option>
                                </select>
                            </div>
                        </div>
                        <div class="col-md-2">
                            <div class="mb-3">
                                <label class="form-label">&nbsp;</label>
                                <button type="submit" class="btn btn-primary d-block w-100">
                                    <i class="fas fa-plus me-1"></i>Add User
                                </button>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    `;
    
    formContainer.innerHTML = formHtml;
    
    // Add form submit handler
    document.getElementById('evidence-user-access-form').addEventListener('submit', function(e) {
        e.preventDefault();
        addEvidenceUserAccess(evidenceId);
    });
}

/**
 * Load the evidence users table
 * @param {string} evidenceId - The evidence ID
 */
function loadEvidenceUsersTable(evidenceId) {
    const tableContainer = document.getElementById('evidence-users-table-container');
    
    if (!tableContainer) {
        console.warn('Evidence users table container not found');
        return;
    }
    
    // Show loading state
    tableContainer.innerHTML = `
        <div class="text-center py-4">
            <i class="fas fa-spinner fa-spin fa-2x mb-2"></i>
            <p class="mb-0">Loading users...</p>
        </div>
    `;
    
    // Make AJAX request to get users table
    fetch(evidenceData.ajax_url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
            action: 'get_evidence_users_table',
            evidence_id: evidenceId,
            evidence_access_nonce: evidenceData.evidence_access_nonce
        })
    })
    .then(response => response.json())
    .then(data => {
        if (data.success) {
            tableContainer.innerHTML = data.data.html;
            
            // Add event listeners for role changes and user removal
            addEvidenceUserEventListeners(evidenceId);
        } else {
            tableContainer.innerHTML = `<div class="alert alert-danger">${data.data.message}</div>`;
        }
    })
    .catch(error => {
        console.error('Error loading users table:', error);
        tableContainer.innerHTML = '<div class="alert alert-danger">Error loading users table.</div>';
    });
}

/**
 * Load pending invitations
 * @param {string} evidenceId - The evidence ID
 */
function loadEvidencePendingInvitations(evidenceId) {
    const container = document.getElementById('evidence-pending-invitations-container');
    
    if (!container) {
        console.warn('Pending invitations container not found');
        return;
    }
    
    // Make AJAX request to get pending invitations
    fetch(evidenceData.ajax_url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
            action: 'get_evidence_pending_invitations',
            evidence_id: evidenceId,
            evidence_access_nonce: evidenceData.evidence_access_nonce
        })
    })
    .then(response => response.json())
    .then(data => {
        if (data.success && data.data.invitations.length > 0) {
            let html = '<div class="card">';
            html += '<div class="card-header"><h6 class="mb-0">Pending Invitations</h6></div>';
            html += '<div class="card-body">';
            html += '<div class="table-responsive">';
            html += '<table class="table table-sm">';
            html += '<thead><tr><th>Email</th><th>Role</th><th>Sent</th><th>Actions</th></tr></thead>';
            html += '<tbody>';
            
            data.data.invitations.forEach(invitation => {
                const sentDate = new Date(invitation.invited_date * 1000).toLocaleDateString();
                const roleClass = invitation.role === 'admin' ? 'bg-success' : 
                                 invitation.role === 'read_write' ? 'bg-warning' : 'bg-secondary';
                
                html += '<tr>';
                html += `<td>${invitation.user_email}</td>`;
                html += `<td><span class="badge ${roleClass}">${invitation.role.replace('_', ' ')}</span></td>`;
                html += `<td>${sentDate}</td>`;
                html += '<td>';
                html += `<button class="btn btn-sm btn-outline-danger" onclick="cancelEvidenceInvitation('${evidenceId}', '${invitation.invitation_token}')">`;
                html += '<i class="fas fa-times"></i> Cancel';
                html += '</button>';
                html += '</td>';
                html += '</tr>';
            });
            
            html += '</tbody></table></div></div></div>';
            container.innerHTML = html;
        } else {
            container.innerHTML = '';
        }
    })
    .catch(error => {
        console.error('Error loading pending invitations:', error);
        container.innerHTML = '';
    });
}

/**
 * Resend an evidence invitation
 * @param {string} evidenceId - The evidence ID
 * @param {string} userEmail - The user email
 * @param {string} role - The user role
 */
function resendEvidenceInvitation(evidenceId, userEmail, role) {
    console.log('Evidence Access Debug - Resending invitation:', evidenceId, userEmail, role);
    
    const formData = new FormData();
    formData.append('action', 'send_evidence_invitation');
    formData.append('evidence_id', evidenceId);
    formData.append('user_email', userEmail);
    formData.append('role', role);
    formData.append('evidence_access_nonce', evidenceData.evidence_access_nonce);
    
    fetch(evidenceData.ajax_url, {
        method: 'POST',
        body: formData
    })
    .then(response => response.json())
    .then(data => {
        if (data.success) {
            // Show success message and refresh page
            showEvidenceAlert('success', 'Invitation resent successfully');
            setTimeout(() => {
                location.reload();
            }, 2000);
        } else {
            showEvidenceAlert('danger', data.data.message || 'Failed to resend invitation');
        }
    })
    .catch(error => {
        console.error('Error resending invitation:', error);
        showEvidenceAlert('danger', 'Error resending invitation. Please try again.');
    });
}

/**
 * Cancel an evidence invitation
 * @param {string} evidenceId - The evidence ID
 * @param {string} invitationToken - The invitation token
 */
function cancelEvidenceInvitation(evidenceId, invitationToken) {
    // Populate the modal with the data
    document.getElementById('cancel-evidence-id').value = evidenceId;
    document.getElementById('cancel-invitation-token').value = invitationToken;
    
    // Show the modal
    const modal = new bootstrap.Modal(document.getElementById('cancelEvidenceInvitationModal'));
    modal.show();
}

/**
 * Confirm cancel evidence invitation (called from modal)
 */
function confirmCancelEvidenceInvitation() {
    const evidenceId = document.getElementById('cancel-evidence-id').value;
    const invitationToken = document.getElementById('cancel-invitation-token').value;
    
    const formData = new FormData();
    formData.append('action', 'cancel_evidence_invitation');
    formData.append('evidence_id', evidenceId);
    formData.append('invitation_token', invitationToken);
    formData.append('evidence_access_nonce', evidenceData.evidence_access_nonce);
    
    fetch(evidenceData.ajax_url, {
        method: 'POST',
        body: formData
    })
    .then(response => response.json())
    .then(data => {
        if (data.success) {
            console.log('Evidence Access Debug - Invitation cancelled successfully');
            
            // Close the modal
            const modal = bootstrap.Modal.getInstance(document.getElementById('cancelEvidenceInvitationModal'));
            modal.hide();
            
            // Show success message briefly, then refresh the page to update the table
            showEvidenceAlert('success', 'Invitation cancelled successfully');
            
            // Refresh page after a short delay to show the success message
            setTimeout(() => {
                location.reload();
            }, 2000);
        } else {
            showEvidenceAlert('danger', data.data.message);
        }
    })
    .catch(error => {
        console.error('Error cancelling invitation:', error);
        showAlert('danger', 'Error cancelling invitation.');
    });
}

/**
 * Add user access to evidence
 * @param {string} evidenceId - The evidence ID
 */
function addEvidenceUserAccess(evidenceId) {
    console.log('Evidence Access Debug - addEvidenceUserAccess called with evidenceId:', evidenceId);
    
    // Check if evidenceData object exists
    if (typeof evidenceData === 'undefined') {
        console.error('Evidence Access Debug - evidenceData object is undefined');
        showEvidenceAlert('danger', 'Evidence data not loaded. Please refresh the page and try again.');
        return;
    }
    
    const form = document.getElementById('evidence-user-access-form');
    if (!form) {
        console.error('Evidence Access Debug - Form not found with ID: evidence-user-access-form');
        showEvidenceAlert('danger', 'Form not found. Please refresh the page and try again.');
        return;
    }
    
    console.log('Evidence Access Debug - Form found:', form);
    console.log('Evidence Access Debug - Form data-evidence-id attribute:', form.getAttribute('data-evidence-id'));
    
    // Get form data manually since we need to convert permissions[] to role
    const userEmail = form.querySelector('input[name="user_email"]')?.value || 
                     form.querySelector('#evidence_user_email')?.value;
    
    // Get selected permissions (checkboxes) and convert to role
    const selectedPermissions = form.querySelectorAll('input[name="permissions[]"]:checked');
    let role = '';
    
    if (selectedPermissions.length > 0) {
        // Use the first selected permission as the role
        role = selectedPermissions[0].value;
    }
    
    console.log('Evidence Access Debug - Extracted form data:');
    console.log('User Email:', userEmail);
    console.log('Selected Role:', role);
    console.log('Evidence ID:', evidenceId);
    
    // Validate form data
    if (!userEmail) {
        showEvidenceAlert('danger', 'Please enter a user email address.');
        return;
    }
    
    if (!role) {
        showEvidenceAlert('danger', 'Please select at least one permission level.');
        return;
    }
    
    // Create FormData with the correct structure for the backend
    const formData = new FormData();
    formData.append('action', 'send_evidence_invitation');
    formData.append('evidence_id', evidenceId);
    formData.append('user_email', userEmail);
    formData.append('role', role);
    formData.append('evidence_access_nonce', evidenceData.evidence_access_nonce);
    
    // Log form data for debugging
    console.log('Evidence Access Debug - Final Form Data:');
    console.log('AJAX URL:', evidenceData.ajax_url);
    for (let [key, value] of formData.entries()) {
        console.log(`${key}: ${value}`);
    }
    
    // Show loading state
    const submitBtn = form.querySelector('button[type="submit"]');
    const originalText = submitBtn.innerHTML;
    submitBtn.innerHTML = '<i class="fas fa-spinner fa-spin me-1"></i>Sending...';
    submitBtn.disabled = true;
    
    fetch(evidenceData.ajax_url, {
        method: 'POST',
        body: formData
    })
    .then(response => {
        console.log('Evidence Access Debug - Response Status:', response.status);
        console.log('Evidence Access Debug - Response Headers:', response.headers);
        return response.json();
    })
    .then(data => {
        console.log('Evidence Access Debug - Response Data:', data);
        console.log('Evidence Access Debug - data.success:', data.success);
        console.log('Evidence Access Debug - data.data:', data.data);
        
        if (data.success) {
            console.log('Evidence Access Debug - Processing success response');
            
            // Clear form and any existing alerts
            form.reset();
            clearEvidenceAlerts();
            
            // Also manually clear the evidence ID to prevent reuse
            const evidenceIdInput = form.querySelector('input[name="evidence_id"]');
            if (evidenceIdInput) {
                evidenceIdInput.value = '';
            }
            
            // Show success message first
            const successMessage = (data.data && data.data.invitation_data && data.data.invitation_data.existing_user) 
                ? 'User added successfully' 
                : 'Invitation sent successfully';
            
            console.log('Evidence Access Debug - Showing success message:', successMessage);
            showEvidenceAlert('success', successMessage);
            
            // Refresh page after a short delay to update the access table
            console.log('Evidence Access Debug - Scheduling page refresh');
            setTimeout(() => {
                location.reload();
            }, 2000);
            
        } else {
            console.error('Evidence Access Debug - Processing error response:', data);
            // Show error message in the modal
            const errorMessage = data.data && data.data.message ? data.data.message : 'An error occurred while processing the invitation.';
            console.log('Evidence Access Debug - Showing error message:', errorMessage);
            showEvidenceAlert('danger', errorMessage);
        }
    })
    .catch(error => {
        console.error('Evidence Access Debug - Fetch Error:', error);
        showEvidenceAlert('danger', 'Error adding user access. Please try again.');
    })
    .finally(() => {
        // Restore button state
        submitBtn.innerHTML = originalText;
        submitBtn.disabled = false;
    });
}

/**
 * Add event listeners for evidence user management
 * @param {string} evidenceId - The evidence ID
 */
function addEvidenceUserEventListeners(evidenceId) {
    // Edit permissions button listeners
    document.querySelectorAll('.update-permissions').forEach(button => {
        console.log('Adding click listener to update-permissions button');
        button.addEventListener('click', function() {
            console.log('Update permissions button clicked');
            const userId = this.dataset.userId;
            const userName = this.dataset.userName;
            const userEmail = this.dataset.userEmail;
            const userRole = this.dataset.userRole;
            
            // Populate the edit modal
            document.getElementById('evidence-user_id').value = userId;
            document.getElementById('evidence-evidence_id').value = evidenceId;
            document.getElementById('evidence-user_email').value = userEmail;
            
            // Set the current role as selected
            document.querySelectorAll('input[name="permissions"]').forEach(radio => {
                radio.checked = false;
            });
            const currentRoleRadio = document.getElementById(`update-evidence-permission-${userRole}`);
            if (currentRoleRadio) {
                currentRoleRadio.checked = true;
            }
            
            // Show/hide ownership option based on current user permissions
            const ownerPermissionOption = document.getElementById('owner-permission-option');
            
            // Only show owner option if current user is the evidence owner
            if (ownerPermissionOption) {
                if (evidenceData.is_evidence_owner) {
                    ownerPermissionOption.style.display = 'block';
                } else {
                    ownerPermissionOption.style.display = 'none';
                }
            }
            
            // Show the modal
            const modal = new bootstrap.Modal(document.getElementById('updateEvidencePermissionsModal'));
            modal.show();
        });
    });
    
    // Remove user listeners
    document.querySelectorAll('.remove-user').forEach(button => {
        console.log('Adding click listener to remove-user button');
        button.addEventListener('click', function() {
            console.log('Remove user button clicked');
            const userId = this.dataset.userId;
            const userName = this.dataset.userName;
            
            // Populate the delete modal
            document.getElementById('delete-evidence-user-id').value = userId;
            document.getElementById('delete-evidence-evidence-id').value = evidenceId;
            
            // Show the modal
            const modal = new bootstrap.Modal(document.getElementById('deleteEvidenceUserModal'));
            modal.show();
        });
    });
    
    // Cancel invitation listeners (these use onclick attributes, so no need for event listeners)
    // The cancelEvidenceInvitation function is called directly via onclick
}

// Flag to ensure we only add the evidence form submission listener once
let evidenceFormListenerAdded = false;

/**
 * Initialize evidence modal event listeners
 */
function initializeEvidenceModalEventListeners() {
    // Evidence user access form submission (use event delegation for dynamically created forms)
    if (!evidenceFormListenerAdded) {
        document.addEventListener('submit', function(e) {
            // Check if the submitted form is an evidence user access form
            if (e.target.id === 'evidence-user-access-form') {
                e.preventDefault();
                console.log('Evidence Access Debug - Evidence form submitted via delegation');
                
                // Get the evidence ID from multiple possible sources
                let evidenceId = e.target.getAttribute('data-evidence-id');
                
                // Check the modal's hidden input first (this is the most reliable for the modal form)
                if (!evidenceId) {
                    const evidenceIdInput = e.target.querySelector('input[name="evidence_id"]');
                    if (evidenceIdInput) {
                        evidenceId = evidenceIdInput.value;
                        console.log('Evidence Access Debug - Found evidence_id from modal hidden input:', evidenceId);
                    }
                }
                
                // Fallback to global variable if form attribute is not found
                if (!evidenceId && typeof currentEvidenceId !== 'undefined') {
                    evidenceId = currentEvidenceId;
                    console.log('Evidence Access Debug - Using currentEvidenceId as fallback:', evidenceId);
                }
                
                console.log('Evidence Access Debug - Final evidence ID:', evidenceId);
                
                if (!evidenceId) {
                    console.error('Evidence Access Debug - No evidence ID found from any source');
                    showEvidenceAlert('danger', 'Evidence ID not found. Please refresh and try again.');
                    return;
                }
                
                // Use the new function with proper error handling
                console.log('Evidence Access Debug - Calling addEvidenceUserAccess with evidenceId:', evidenceId);
                addEvidenceUserAccess(evidenceId);
            }
        });
        evidenceFormListenerAdded = true;
        console.log('Evidence Access Debug - Form submission listener added');
    }
    
    // Update permissions form submission
    const updateForm = document.getElementById('update-evidence-permissions-form');
    if (updateForm) {
        updateForm.addEventListener('submit', function(e) {
            e.preventDefault();
            
            const userId = document.getElementById('evidence-user_id').value;
            const evidenceId = document.getElementById('evidence-evidence_id').value;
            const selectedRole = document.querySelector('input[name="permissions"]:checked');
            
            if (!selectedRole) {
                alert('Please select a permission level.');
                return;
            }
            
            const newRole = selectedRole.value;
            
            // Close the modal
            const modal = bootstrap.Modal.getInstance(document.getElementById('updateEvidencePermissionsModal'));
            modal.hide();
            
            // Update the role
            updateEvidenceUserRole(evidenceId, userId, newRole);
        });
    }
    
    // Delete user confirmation
    const deleteButton = document.getElementById('confirm-delete-evidence-user');
    if (deleteButton) {
        deleteButton.addEventListener('click', function() {
            const userId = document.getElementById('delete-evidence-user-id').value;
            const evidenceId = document.getElementById('delete-evidence-evidence-id').value;
            
            // Close the modal
            const modal = bootstrap.Modal.getInstance(document.getElementById('deleteEvidenceUserModal'));
            modal.hide();
            
            // Remove the user
            removeEvidenceUserAccess(evidenceId, userId);
        });
    }
    
    // Cancel invitation confirmation
    const cancelInvitationButton = document.getElementById('confirm-cancel-evidence-invitation');
    if (cancelInvitationButton) {
        cancelInvitationButton.addEventListener('click', function() {
            confirmCancelEvidenceInvitation();
        });
    }
}

/**
 * Invite user to evidence
 * @param {string} evidenceId - The evidence ID
 * @param {string} userEmail - The user email
 * @param {string} role - The role to assign
 */
function inviteEvidenceUser(evidenceId, userEmail, role) {
    const formData = new FormData();
    formData.append('action', 'invite_evidence_user');
    formData.append('evidence_id', evidenceId);
    formData.append('user_email', userEmail);
    formData.append('role', role);
    formData.append('evidence_access_nonce', evidenceData.evidence_access_nonce);
    
    fetch(evidenceData.ajax_url, {
        method: 'POST',
        body: formData
    })
    .then(response => response.json())
    .then(data => {
        if (data.success) {
            // Clear form and show success message in the modal (don't close it immediately)
            document.getElementById('evidence-user-access-form').reset();
            showEvidenceAlert('success', 'User invited successfully!');
            
            // Reload users table and pending invitations if functions exist
            if (typeof loadEvidenceUsersTable === 'function') {
                loadEvidenceUsersTable(evidenceId);
            }
            if (typeof loadEvidencePendingInvitations === 'function') {
                loadEvidencePendingInvitations(evidenceId);
            }
            
            // Auto-close modal and refresh page after user sees the success message
            setTimeout(() => {
                const modal = bootstrap.Modal.getInstance(document.getElementById('evidenceUserAccessModal'));
                if (modal) {
                    modal.hide();
                }
                location.reload();
            }, 3000); // Give user time to see the success message
        } else {
            // Show error message in the modal
            const errorMessage = data.data && data.data.message ? data.data.message : (data.message || 'Failed to invite user');
            showEvidenceAlert('danger', errorMessage);
        }
    })
    .catch(error => {
        console.error('Error inviting user:', error);
        showEvidenceAlert('danger', 'Error inviting user. Please try again.');
    });
}

/**
 * Update evidence user role
 * @param {string} evidenceId - The evidence ID
 * @param {string} userId - The user ID
 * @param {string} newRole - The new role
 */
function updateEvidenceUserRole(evidenceId, userId, newRole) {
    const formData = new FormData();
    formData.append('action', 'update_evidence_user_access');
    formData.append('evidence_id', evidenceId);
    formData.append('user_id', userId);
    formData.append('role', newRole);
    formData.append('evidence_access_nonce', evidenceData.evidence_access_nonce);
    
    fetch(evidenceData.ajax_url, {
        method: 'POST',
        body: formData
    })
    .then(response => response.json())
    .then(data => {
        if (data.success) {
            const message = data.data.message || 'User role updated successfully';
            showAlert('success', message);
            // Reload the page to reflect changes
            location.reload();
        } else {
            showAlert('danger', data.data.message);
        }
    })
    .catch(error => {
        console.error('Error updating user role:', error);
        showAlert('danger', 'Error updating user role.');
    });
}

/**
 * Remove evidence user access
 * @param {string} evidenceId - The evidence ID
 * @param {string} userId - The user ID
 */
function removeEvidenceUserAccess(evidenceId, userId) {
    const formData = new FormData();
    formData.append('action', 'remove_evidence_user_access');
    formData.append('evidence_id', evidenceId);
    formData.append('user_id', userId);
    formData.append('evidence_access_nonce', evidenceData.evidence_access_nonce);
    
    fetch(evidenceData.ajax_url, {
        method: 'POST',
        body: formData
    })
    .then(response => response.json())
    .then(data => {
        if (data.success) {
            showAlert('success', data.data.message);
            // Reload the page to reflect changes
            location.reload();
        } else {
            showAlert('danger', data.data.message);
        }
    })
    .catch(error => {
        console.error('Error removing user access:', error);
        showAlert('danger', 'Error removing user access.');
    });
}

/**
 * Clear all existing alerts from evidence modals
 */
function clearEvidenceAlerts() {
    // Remove alerts from all evidence-related modals
    const modalSelectors = [
        '#evidenceUserAccessModal .alert',
        '#updateEvidencePermissionsModal .alert',
        '#deleteEvidenceUserModal .alert',
        '.modal.show .alert' // Fallback for any open modal
    ];
    
    modalSelectors.forEach(selector => {
        const alerts = document.querySelectorAll(selector);
        alerts.forEach(alert => alert.remove());
    });
}

/**
 * Show alert message in the evidence user access modal
 * @param {string} type - Alert type (success, danger, warning, info)
 * @param {string} message - Alert message
 */
function showEvidenceAlert(type, message) {
    console.log('Evidence Access Debug - showEvidenceAlert called with type:', type, 'message:', message);
    
    // Remove any existing alerts first
    const existingAlerts = document.querySelectorAll('#evidenceUserAccessModal .alert');
    console.log('Evidence Access Debug - Removing', existingAlerts.length, 'existing alerts');
    existingAlerts.forEach(alert => alert.remove());
    
    // Create alert element
    const alert = document.createElement('div');
    alert.className = `alert alert-${type} alert-dismissible fade show mt-3`;
    alert.innerHTML = `
        <i class="fas ${type === 'success' ? 'fa-check-circle' : 'fa-exclamation-triangle'} me-2"></i>
        ${message}
        <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
    `;
    
    console.log('Evidence Access Debug - Created alert element with class:', alert.className);
    
    // Insert after the fieldset, not at the top of modal body
    const modalBody = document.querySelector('#evidenceUserAccessModal .modal-body');
    console.log('Evidence Access Debug - Modal body found:', !!modalBody);
    
    if (modalBody) {
        // Try to find the fieldset and insert after it
        const fieldset = modalBody.querySelector('fieldset');
        if (fieldset) {
            // Insert after the fieldset
            fieldset.parentNode.insertBefore(alert, fieldset.nextSibling);
            console.log('Evidence Access Debug - Alert inserted after fieldset');
        } else {
            // Fallback: insert at the end of the permissions-invite div
            const permissionsDiv = modalBody.querySelector('.permissions-invite');
            if (permissionsDiv) {
                permissionsDiv.appendChild(alert);
                console.log('Evidence Access Debug - Alert inserted at end of permissions-invite div');
            } else {
                // Final fallback: insert at top of modal body
                modalBody.insertBefore(alert, modalBody.firstChild);
                console.log('Evidence Access Debug - Alert inserted at top of modal body (fallback)');
            }
        }
        
        // Auto-dismiss success messages after 5 seconds, keep error messages until manually dismissed
        if (type === 'success') {
            console.log('Evidence Access Debug - Setting auto-dismiss timer for success message');
            setTimeout(() => {
                if (alert.parentNode) {
                    console.log('Evidence Access Debug - Auto-dismissing success alert');
                    alert.remove();
                }
            }, 5000);
        }
    } else {
        // Fallback: try to find any evidence modal that's currently open
        const openModal = document.querySelector('.modal.show .modal-body');
        if (openModal) {
            openModal.insertBefore(alert, openModal.firstChild);
            
            if (type === 'success') {
                setTimeout(() => {
                    if (alert.parentNode) {
                        alert.remove();
                    }
                }, 5000);
            }
        } else {
            // Last resort: show a browser alert
            alert(message);
        }
    }
}

/**
 * Show alert message in the modal (generic function)
 * @param {string} type - Alert type (success, danger, warning, info)
 * @param {string} message - Alert message
 */
function showAlert(type, message) {
    // Use the evidence-specific alert function
    showEvidenceAlert(type, message);
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
    // Initialize modal event listeners
    initializeEvidenceModalEventListeners();
    
    // Initialize event listeners for access card table buttons (always present on single evidence page)
    const accessCardButtons = document.querySelectorAll('.update-permissions, .remove-user, .cancel-invitation');
    if (accessCardButtons.length > 0) {
        console.log('Evidence access card buttons found, initializing event listeners');
        
        // Get evidence ID from the page (should be available in a data attribute or similar)
        const evidenceId = getEvidenceIdFromPage();
        if (evidenceId) {
            addEvidenceUserEventListeners(evidenceId);
        }
    }
    
    // Check if evidence access modal exists on the page
    if (document.getElementById('evidenceUserAccessModal')) {
        console.log('Evidence access modal found, JavaScript initialized');
        
        // Log evidenceData object for debugging
        console.log('Evidence Access Debug - evidenceData object:', evidenceData);
        
        if (typeof evidenceData === 'undefined') {
            console.error('Evidence Access Debug - evidenceData object is undefined!');
        } else {
            console.log('Evidence Access Debug - AJAX URL:', evidenceData.ajax_url);
            console.log('Evidence Access Debug - Evidence Access Nonce:', evidenceData.evidence_access_nonce);
            console.log('Evidence Access Debug - Current User ID:', evidenceData.current_user_id);
        }
    }
});

/**
 * Get evidence ID from the page
 * @returns {string|null} The evidence ID or null if not found
 */
function getEvidenceIdFromPage() {
    // Try to get evidence ID from data attributes on buttons
    const button = document.querySelector('.update-permissions, .remove-user, .cancel-invitation');
    if (button && button.dataset.evidenceId) {
        return button.dataset.evidenceId;
    }
    
    // Try to get from URL or other page elements
    const pathParts = window.location.pathname.split('/');
    const evidenceIndex = pathParts.indexOf('evidence');
    if (evidenceIndex !== -1 && pathParts[evidenceIndex + 1]) {
        return pathParts[evidenceIndex + 1];
    }
    
    return null;
}
